<template>
    <div v-if="account">
        <div class="grid grid-cols-4 gap-4" @keydown.ctrl.shift.enter="save">
            <general-input
                ref="accountNoInput"
                v-model="account.accountNo"
                label="Kontonummer"
                placeholder="1234"
                :is-valid="editMode ? validateAccountNo : validateBookkeepingAccountNo"
                data-cy="account-no-input"
                class="col-span-2"
                :custom-event-listener="accountNoInputValidation"
                :disabled="editMode"
            />
            <general-input
                v-model="account.name"
                label="Kontonavn"
                placeholder=""
                :is-valid="validateName"
                data-cy="account-name-input"
                class="col-span-2"
            />
            <general-input
                v-model="keywords"
                label="Nøkkelord"
                placeholder=""
                class="col-span-2"
            />
            <general-input
                v-model="account.description"
                label="Kontobeskrivelse"
                placeholder=""
                class="col-span-2"
            />

            <div class="flex col-span-2">
                <label for="vatPickerInputContainer">Standard MVA-kode</label>
                <vat-picker
                    v-model:vat-option="vatOption"
                    class="w-full"
                    :amount="0"
                    :disable-teleport="disableTeleport"
                />
            </div>

            <div class="flex items-center col-span-2">
                <USwitch
                    v-model="account.isAccrual"
                    label="Periodiseringskonto"
                    class="mx-2"
                    size="lg"
                    autocomplete="off"
                />
            </div>
        </div>

        <div class="flex md:col-span-2 gap-2 justify-end">
            <UButton
                size="sm"
                variant="outline"
                color="neutral"
                class="mt-4 lg:mt-6"
                @click="cancel"
            >
                Avbryt
            </UButton>
            <UButton
                type="submit"
                size="sm"
                icon="i-mdi-content-save"
                class="mt-4 lg:mt-6 w-full lg:w-fit"
                :disabled="!isValid"
                data-cy="click-save-button"
                @click="save"
            >
                Lagre
            </UButton>
        </div>
    </div>
    <div v-else/>
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, PropType, ref, useTemplateRef} from 'vue'
import {accountNameRegex, validAccountNoRegex} from '@/services/validators.service'
import {BookkeepingAccount, UPDATE_BOOKKEEPING_ACCOUNT} from '@/bookkeeping-account/BookkeepingAccount'
import emitter from '@/services/infrastructure/emitter'
import {bookkeepingAccounts, defaultBookkeepingAccount, fetchBookkeepingAccount, upsertBookkeepingAccount} from '@/bookkeeping-account/bookkeepingAccount.service'
import {useVatService} from '@/vat/vat.service'

import GeneralInput from '@/components/GeneralInput.vue'
import {accountNoInputValidation, validateAccountNo, validateBookkeepingAccountNo, validateName} from '@/bookkeeping-account/BookeepingAccountValidator'
import VatPicker from '@/vat/VatPicker.vue'
import type {ComponentExposed} from 'vue-component-type-helpers'
import {VatOption, defaultVatOption} from '@/vat/types/VatOptions'

const account = ref<BookkeepingAccount>(defaultBookkeepingAccount())

const {getVatOptionById} = useVatService()

const accountNoInput = useTemplateRef<ComponentExposed<typeof GeneralInput>>('accountNoInput')
const search = ref<string>('0')
const editMode = ref(false)

const vatOption = computed({
    get: () => {
        return getVatOptionById(account.value.vatSuggestionId) || defaultVatOption
    },
    set: (option: VatOption) => {
        search.value = option?.code?.toString() || '0'
        account.value.vatSuggestionId = option?.id
    }
})

const keywords = ref('')

const props = defineProps({
    bookkeepingAccount: {
        type: Object as PropType<BookkeepingAccount>,
        required: true
    },
    disableTeleport: {
        type: Boolean,
        required: false,
        default: false
    }
})

const emit = defineEmits(['save', 'cancel'])

const accountExists = computed(() => {
    if (!validAccountNoRegex.test(account.value.accountNo)) {
        return false
    }
    if (!editMode.value && bookkeepingAccounts?.value) {
        return !bookkeepingAccounts.value.some((acc: BookkeepingAccount) => acc.accountNo === account.value.accountNo)
    }
    return true
})

const isValid = computed(() => {
    if (!accountNameRegex.test(account.value.name)) {
        return false
    }
    if (!editMode.value && !accountExists.value) {
        return false
    }
    if (editMode.value && !isChanged.value) {
        return false
    }
    return true
})

const isChanged = computed(() => {
    return account.value.name !== props.bookkeepingAccount.name
        || account.value.description !== props.bookkeepingAccount.description
        || account.value.keywords !== props.bookkeepingAccount.keywords
        || account.value.vatSuggestionId !== props.bookkeepingAccount.vatSuggestionId
        || account.value.isAccrual !== props.bookkeepingAccount.isAccrual
})

const splitIntoKeywords = () => {
    const words = keywords.value.split(/[ ,]+/)
    account.value.keywords = words.filter((word) => word !== '')
}

const save = async () => {
    if (!isValid.value) return
    splitIntoKeywords()
    const newAccount = await upsertBookkeepingAccount(account.value)
    await fetchBookkeepingAccount()
    emitter.emit(UPDATE_BOOKKEEPING_ACCOUNT)
    emit('save', newAccount)
}


const cancel = () => {
    account.value = defaultBookkeepingAccount()
    search.value = '0'
    editMode.value = false
    emit('cancel')
}

onMounted(async () => {
    if (props.bookkeepingAccount?.accountNo != defaultBookkeepingAccount().accountNo) {
        editMode.value = true
    }
    account.value = {
        ...props.bookkeepingAccount
    }
    keywords.value = account.value.keywords.join(', ')
    await nextTick(() => {
        accountNoInput.value?.focus()
    })
})

</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>


<style>


</style>